import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { orderBy } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg } from '../../utils/makeToast'
import { fetchClientsUtil } from '../../utils/fetchUtils'

// Fetches Clients that has users tenantId and the active flag is true
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      const sort = settings.sort
      const items = await fetchClientsUtil(auth.userInfo.tenantId)

      // For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
      // const parsedClientsData = JSON.parse(JSON.stringify(clientsData))
      let sortedClients
      if (sort === 'date') {
        sortedClients = orderBy(items, ['createdOn'], ['asc'])
      } else if (sort === 'dateM') {
        sortedClients = orderBy(items, ['createdOn'], ['desc'])
      } else if (sort === 'name') {
        sortedClients = orderBy(items, [client => client.name.toLowerCase()], ['asc'])
      }
      return sortedClients
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Creates a new client item with name provided by user and then fetches clients again
export const createClient = createAsyncThunk(
  'clients/createClient',
  async ({ clientName }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState()
      // Creates a new item in clients table save the id, name and tenantId that is the same as the user since only companyAdmin can create this
      let newClient = {
        id: nanoid(),
        name: clientName,
        tenantId: auth.userInfo.tenantId,
        activeFlag: true,
        offlineIds: ['dummyId'],
      }
      await API.graphql(graphqlOperation(mutations.createClients, { input: newClient }))
      dispatch(fetchClients())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Updates the name of the clients & fetches the clients again
export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ client, clientName }, { dispatch, rejectWithValue }) => {
    // Gets the original client from datastore then updates it
    try {
      const {
        data: { getClients },
      } = await API.graphql(graphqlOperation(queries.getClients, { id: client.id }))
      delete getClients._deleted
      delete getClients._lastChangedAt
      delete getClients.projects
      getClients.name = clientName
      await API.graphql(graphqlOperation(mutations.updateClients, { input: getClients }))
      dispatch(fetchClients())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

// Deletes the clients from id & fetches the clients again
export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async ({ client }, { dispatch, rejectWithValue }) => {
    // Gets the original client from datastore then deletes it along with all it's children projects & grids since it's a one to many relationship
    try {
      await API.graphql(graphqlOperation(mutations.deleteClientById, { clientId: client.id }))
      dispatch(fetchClients())
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const clientsSlice = createSlice({
  name: 'clients',
  initialState: {
    clientsData: [],
    fetchClientsApiStatus: 'idle',
    createOrUpdateClientApiStatus: 'idle',
    deleteClientApiStatus: 'idle',
    clientModalOpenStatus: false,
    clientDeleteDialogOpenStatus: false,
    currentClient: null,
    error: null,
  },
  reducers: {
    setClientsModalOpenStatus: (state, action) => {
      state.clientModalOpenStatus = action.payload.status
      state.currentClient = action.payload.client
    },
    setClientsDeleteDialogOpenStatus: (state, action) => {
      state.clientDeleteDialogOpenStatus = action.payload.status
      state.currentClient = action.payload.client
    },
    setClientFetchApiStatus: (state, action) => {
      state.fetchClientsApiStatus = 'loading'
    },
    sortClients: (state, action) => {
      if (action.payload === 'date') {
        state.clientsData = orderBy(state.clientsData, ['createdOn'], ['asc'])
      } else if (action.payload === 'dateM') {
        state.clientsData = orderBy(state.clientsData, ['createdOn'], ['desc'])
      } else if (action.payload === 'name') {
        state.clientsData = orderBy(
          state.clientsData,
          [client => client.name.toLowerCase()],
          ['asc']
        )
      }
    },
    clearClientsData: (state, action) => {
      state.clientsData = []
    },
  },
  extraReducers: {
    [fetchClients.pending]: (state, action) => {
      state.fetchClientsApiStatus = 'loading'
    },
    [fetchClients.fulfilled]: (state, action) => {
      state.fetchClientsApiStatus = 'succeeded'
      state.clientsData = action.payload
    },
    [fetchClients.rejected]: (state, action) => {
      state.fetchClientsApiStatus = 'failed'
      state.error = action.error.message
    },
    [createClient.pending]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'loading'
    },
    [createClient.fulfilled]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'succeeded'
      state.clientModalOpenStatus = false
    },
    [createClient.rejected]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'failed'
      state.clientModalOpenStatus = false
      state.error = action.error.message
    },
    [updateClient.pending]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'loading'
    },
    [updateClient.fulfilled]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'succeeded'
      state.clientModalOpenStatus = false
    },
    [updateClient.rejected]: (state, action) => {
      state.createOrUpdateClientApiStatus = 'failed'
      state.clientModalOpenStatus = false
      state.error = action.error.message
    },
    [deleteClient.pending]: (state, action) => {
      state.deleteClientApiStatus = 'loading'
    },
    [deleteClient.fulfilled]: (state, action) => {
      state.deleteClientApiStatus = 'succeeded'
      state.clientDeleteDialogOpenStatus = false
    },
    [deleteClient.rejected]: (state, action) => {
      state.deleteClientApiStatus = 'failed'
      state.clientDeleteDialogOpenStatus = false
      state.error = action.error.message
    },
  },
})

export const {
  setClientsModalOpenStatus,
  setClientsDeleteDialogOpenStatus,
  setClientFetchApiStatus,
  sortClients,
  clearClientsData,
} = clientsSlice.actions

export default clientsSlice.reducer
